import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import Swal from "sweetalert2";

// Icon

// Context
import PPAssessmentContext from "../../../context/psikotest/PPAssessmentContext";
import SnackbarContext from "../../../context/psikotest/SnackbarContext";
import WebSocketContext from "../../../context/WebSocketContext";

// Controller
import ParticipantSession from "../../../controller/psikotest/psikotest_participant/session";

// Helper
import logRender from "../../../helper/psikotest/logRender";
import errorHandler from "../../../helper/psikotest/errorHandler";
import {
  createCoalescedRequestState,
  isExplicitNoOpenSubtestError,
} from "../../psikotest_system/assessment/activeSubtestControl";

// Component
import Header from "../../../components/psikotest/psikotest_participant/Header";
// Intelligence
import SubtestCfit from "../../../components/psikotest/psikotest_participant/SubtestCfit";
import SubtestIst from "../../../components/psikotest/psikotest_participant/SubtestIst";
// Personality
import SubtestMbti from "../../../components/psikotest/psikotest_participant/SubtestMbti";
import SubtestPapi from "../../../components/psikotest/psikotest_participant/SubtestPapi";
import SubtestEpps from "../../../components/psikotest/psikotest_participant/SubtestEpps";
import SubtestSrq20 from "../../../components/psikotest/psikotest_participant/SubtestSrq20";
import SubtestMmpi from "../../../components/psikotest/psikotest_participant/SubtestMmpi";
import SubtestBrs from "../../../components/psikotest/psikotest_participant/SubtestBrs";
// Behavioural
import SubtestRac from "../../../components/psikotest/psikotest_participant/SubtestRac";
import SubtestDisc from "../../../components/psikotest/psikotest_participant/SubtestDisc";
import SubtestResilience from "../../../components/psikotest/psikotest_participant/SubtestResilience";
import {
  ConsentStartPanel,
  FullscreenReturnBar,
  IntegrityWarning,
  ProtectedTestContent,
} from "../../../components/shared/sessionIntegrity/SessionIntegrity";
import useSessionIntegrity, {
  IntegrityStateEvent,
} from "../../../components/shared/sessionIntegrity/useSessionIntegrity";

function Session() {
  // Context
  const { assessment_participant, timeRemaining, setTimeRemaining } = useContext(PPAssessmentContext);
  const { setNotif } = useContext(SnackbarContext);
  const { socket } = useContext(WebSocketContext)
  // Ref
  const webcamRef = useRef<Webcam>(null);
  const pendingAwaySnapshotsRef = useRef<Map<string, string>>(new Map());
  const activeSessionIdentityRef = useRef("");
  const openSubtestRequestStateRef = useRef(createCoalescedRequestState());
  const participantAccessKeyRef = useRef(assessment_participant?.access_key ?? "");
  participantAccessKeyRef.current = assessment_participant?.access_key ?? "";
  const markSnapshotFailureRef = useRef<
    (sourceClientEventId: string, occurredAt: string, reason: string) => void
  >(() => undefined);
  // State
  const [assessment_participant_session, setAssessmentParticipantSession] = useState<TPPAssessmentParticipantSession | undefined>(undefined);
  const [questionaryWithImageLoad, setQuestionaryWithImageLoad] = useState<boolean | undefined>(undefined);
  const [questionaryImageLoadFailed, setQuestionaryImageLoadFailed] = useState<string>("");
  const [imageQuestionaryTotal, setImageQuestionaryTotal] = useState<number>(0);
  const [imageQuestionaryTotalLoaded, setImageQuestionaryTotalLoaded] = useState<number>(0);
  // for state assessmnet is closed by user
  const [close_by_user, setCloseByUser] = useState<boolean>(false);

  const [isCheckActiveSession, setIsCheckActiveSession] = useState<boolean>(false);
  const [isCheckCamera, setIsCheckCamera] = useState<boolean>(false);
  const [cameraAllowed, setCameraAllowed] = useState<boolean>(false);
  const [captureImage, setCaptureImage] = useState<string>();
  // screencap every x second = default 5 minutes
  const [intervalGetImage, setIntervalGetImage] = useState<number>(300);
  // interval check timer x second = default 105 second
  const intervalCheckTimer = 105;

  useEffect(() => {
    logRender({ type: "page", name: "assessment/Session" });
  }, []);

  // async check image
  const loadImage = async (image: HTMLImageElement, totalLoad: number): Promise<number> => {
    return new Promise((resolve, reject) => {
      totalLoad++;
      image.onload = () => {
        resolve(totalLoad);
      };
      image.onerror = () => {
        reject(`Image not found, Question Image: ${totalLoad}`)
      };
    });
  }

  const loadQuestionary = useCallback(async (
    assessment_participant_session: TPPAssessmentParticipantSession | undefined,
    identity: string
  ) => {
    try {
      if (assessment_participant_session) {
        // check if assessment session is completed / done
        if ((assessment_participant_session.remaining_time ?? 0) >= 0) {
          // CFIT image question
          if (assessment_participant_session.type_test === "cfit") {
            // set questionary is with image (cfit default questionary is image)
            setQuestionaryWithImageLoad(true)
            // get all question in subtest id
            const psc = new ParticipantSession();
            let resQusetionaryCfit = await psc.getQuestionaryCfit(assessment_participant_session.id);
            if (activeSessionIdentityRef.current !== identity) return;
            // if return empty array
            if (resQusetionaryCfit.data.list.length < resQusetionaryCfit.data.total_questionary) {
              await psc.createQuestionaryCfit(assessment_participant_session.id);
              if (activeSessionIdentityRef.current !== identity) return;
            }
            // re-fetch questionary data
            resQusetionaryCfit = await psc.getQuestionaryCfit(assessment_participant_session.id);
            if (activeSessionIdentityRef.current !== identity) return;
            setImageQuestionaryTotal(resQusetionaryCfit.data.list.length)
            for (let index = 0, totalLoad = 0; index < resQusetionaryCfit.data.list.length; index++) {
              const questionary: TPPAssessmentSessionCfitQuestionary = resQusetionaryCfit.data.list[index];
              const image = new Image();
              image.src = "/upload/psikotest_participant/get_cfit/" + questionary.cfit_questionary.question_image;
              totalLoad = await loadImage(image, totalLoad)
              if (activeSessionIdentityRef.current !== identity) return;
              setImageQuestionaryTotalLoaded(totalLoad)
            }
            // reset total
            setImageQuestionaryTotalLoaded(0)
          }

          // IST image question
          if (assessment_participant_session.type_test === "ist") {
            // get all question in subtest id
            const psc = new ParticipantSession();
            let resQuestionaryIstImage = await psc.getQuestionaryIstImage(assessment_participant_session.id);
            if (activeSessionIdentityRef.current !== identity) return;
            // check if questionary is avaible
            if (resQuestionaryIstImage.data.list.length > 0) {
              console.log(resQuestionaryIstImage)
              // check if questionary is image
              const withImage = resQuestionaryIstImage.data.list[0]?.ist_questionary_image_id != null
              if (withImage) {
                // set questionary is with image
                setQuestionaryWithImageLoad(true)
                setImageQuestionaryTotal(resQuestionaryIstImage.data.list.length)
                for (let index = 0, totalLoad = 0; index < resQuestionaryIstImage.data.list.length; index++) {
                  const questionary: TPPAssessmentSessionIstQuestionaryImage = resQuestionaryIstImage.data.list[index];
                  const image_questionary = new Image();
                  image_questionary.src = "/upload/psikotest_participant/get_ist_questionary_image/" + questionary.ist_questionary_image;
                  await loadImage(image_questionary, totalLoad)
                  if (activeSessionIdentityRef.current !== identity) return;
                  const image_question = new Image();
                  image_question.src = "/upload/psikotest_participant/get_ist_questionary/" + questionary.question;
                  totalLoad = await loadImage(image_question, totalLoad)
                  if (activeSessionIdentityRef.current !== identity) return;
                  setImageQuestionaryTotalLoaded(totalLoad)
                }
                // reset total
                setImageQuestionaryTotalLoaded(0)
              }
            }
          }

          // RAC image question
          if (assessment_participant_session.type_test === "RAC") {
            // set questionary is with image (rac default questionary is image)
            setQuestionaryWithImageLoad(true)
            // get all question in subtest id
            const psc = new ParticipantSession();
            let resQusetionaryRac = await psc.getQuestionaryRac();
            if (activeSessionIdentityRef.current !== identity) return;
            setImageQuestionaryTotal(resQusetionaryRac.data.list.length)
            for (let index = 0, totalLoad = 0; index < resQusetionaryRac.data.list.length; index++) {
              const questionary: TPPARacQuestionary = resQusetionaryRac.data.list[index];
              const image = new Image();
              image.src = "/upload/psikotest_participant/get_rac/" + questionary.question_image;
              totalLoad = await loadImage(image, totalLoad)
              if (activeSessionIdentityRef.current !== identity) return;
              setImageQuestionaryTotalLoaded(totalLoad)
            }
            // reset total
            setImageQuestionaryTotalLoaded(0)
          }
        }

        // set questionary done load
        if (activeSessionIdentityRef.current === identity) {
          setQuestionaryWithImageLoad(false)
        }
      }
    } catch (error) {
      if (activeSessionIdentityRef.current !== identity) return;
      // show notification
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
      // set load error
      setQuestionaryImageLoadFailed(errorMessage)
    }
  }, [setNotif])

  const fetchOpenSubtestOnce = useCallback(async () => {
    const requestedAccessKey = participantAccessKeyRef.current;
    try {
      // Get active session
      const psc = new ParticipantSession();
      const res = await psc.getOpenSubtest(requestedAccessKey);
      if (participantAccessKeyRef.current !== requestedAccessKey) return;
      const nextIdentity = `${res.data.row?.type_test ?? ""}:${res.data.row?.id ?? ""}`;
      if (nextIdentity !== activeSessionIdentityRef.current) {
        activeSessionIdentityRef.current = nextIdentity;
        setQuestionaryWithImageLoad(undefined);
        setQuestionaryImageLoadFailed("");
        setImageQuestionaryTotal(0);
        setImageQuestionaryTotalLoaded(0);
        setIntervalGetImage(300);
        setCaptureImage(undefined);
        setCloseByUser(false);
        setIsCheckCamera(false);
        setCameraAllowed(false);
        setTimeRemaining(null);
        void loadQuestionary(res.data.row, nextIdentity);
      }
      // set assessment participant session
      if (
        // Intelligence
        res.data.row.type_test === "cfit"
        || res.data.row.type_test === "ist"
        // Personality
        || res.data.row.type_test === "MBTI"
        || res.data.row.type_test === "PAPI"
        || res.data.row.type_test === "EPPS"
        || res.data.row.type_test === "SRQ-20"
        || res.data.row.type_test === "MMPI"
        || res.data.row.type_test === "BRS"
        // Behavioural
        || res.data.row.type_test === "RAC"
        || res.data.row.type_test === "DISC"
        || res.data.row.type_test === "RESILIENCE"
      ) {
        setAssessmentParticipantSession(res.data.row)
      }

      // set snap interval
      if (res.data.row.snap_interval_assessment > 0) {
        setIntervalGetImage(res.data.row.snap_interval_assessment)
      }
      setCloseByUser(false)
    } catch (error) {
      if (participantAccessKeyRef.current !== requestedAccessKey) return;
      if (isExplicitNoOpenSubtestError(error)) {
        activeSessionIdentityRef.current = "";
        setAssessmentParticipantSession(undefined);
        setQuestionaryWithImageLoad(undefined);
        setQuestionaryImageLoadFailed("");
        setImageQuestionaryTotal(0);
        setImageQuestionaryTotalLoaded(0);
        setIntervalGetImage(300);
        setCaptureImage(undefined);
        setCloseByUser(false);
        setTimeRemaining(null);
      }
      // Keep the current session visible for transient network and server errors.
    } finally {
      if (participantAccessKeyRef.current === requestedAccessKey) {
        setIsCheckActiveSession(true);
      }
    }
  }, [loadQuestionary, setTimeRemaining]);

  const getOpenSubtest = useCallback(async () => {
    const requestState = openSubtestRequestStateRef.current;
    if (!requestState.request()) return;
    do {
      await fetchOpenSubtestOnce();
    } while (requestState.complete());
  }, [fetchOpenSubtestOnce]);

  // initial use effect get open session
  useEffect(() => {
    getOpenSubtest();
  }, [getOpenSubtest]);

  // WebSocket tetap menjadi sinyal utama; polling ini memulihkan status bila koneksi terputus.
  useEffect(() => {
    const polling = window.setInterval(() => {
      void getOpenSubtest();
    }, 12000);
    return () => window.clearInterval(polling);
  }, [getOpenSubtest]);

  // Bind WS handler when socket (re)connects
  useEffect(() => {
    if (!socket) {
      return;
    }
    socket.onmessage = (event) => {
      if (typeof event.data === "string") {
        try {
          const envelope = JSON.parse(event.data);
          const wsdata = typeof envelope?.body === "string"
            ? JSON.parse(envelope.body)
            : envelope;
          const scope = wsdata?.scope;
          const target = wsdata?.target;
          const action = wsdata?.action;
          const message = wsdata?.message;
          const messageAssessmentId = wsdata?.assessment_id;
          const belongsToAssessment =
            messageAssessmentId === undefined ||
            Number(messageAssessmentId) === assessment_participant.assessment_id;
          if (scope === "psikotest" && target === "participant" && belongsToAssessment) {
            if (action === "check open session") {
              void getOpenSubtest();
            }
            if (message) {
              setNotif({ type: "info", message: message });
            }
          }
        } catch {
          // Ignore malformed messages and rely on polling.
        }
      }
    };
    return () => {
      socket.onmessage = null;
    };
  }, [socket, getOpenSubtest, setNotif, assessment_participant.assessment_id]);

  const getSession = useCallback(async () => {
    try {
      // make sure not undefined
      if (assessment_participant_session && assessment_participant_session.type_test) {
        const psc = new ParticipantSession();
        const res = await psc.getSession(assessment_participant_session.id, assessment_participant_session.type_test);
        setAssessmentParticipantSession({
          ...assessment_participant_session,
          start_time: res.data.row.start_time,
          end_time: res.data.row.end_time,
          remaining_time: res.data.row.remaining_time,
        })
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }, [assessment_participant_session, setNotif]);

  const captureTabAwaySnapshot = useCallback((event: IntegrityStateEvent) => {
    if (event.phase !== "away") return;
    const image = webcamRef.current?.getScreenshot();
    if (!image) {
      markSnapshotFailureRef.current(
        event.clientEventId,
        event.occurredAt,
        "camera_unavailable"
      );
      return;
    }
    pendingAwaySnapshotsRef.current.set(event.clientEventId, image);
  }, []);
  const uploadTabAwaySnapshot = useCallback(
    async (event: { client_event_id: string; occurred_at: string }) => {
      const image = pendingAwaySnapshotsRef.current.get(event.client_event_id);
      if (!image) {
        markSnapshotFailureRef.current(
          event.client_event_id,
          event.occurred_at,
          "snapshot_not_cached"
        );
        return;
      }
      if (
        !assessment_participant_session?.id ||
        !assessment_participant_session.type_test
      ) return;
      pendingAwaySnapshotsRef.current.delete(event.client_event_id);
      try {
        const controller = new ParticipantSession();
        await controller.saveWebcam(
          assessment_participant_session.id,
          assessment_participant_session.type_test,
          {
            base64_image: image,
            client_event_id: event.client_event_id,
            capture_reason: "tab_away",
          }
        );
      } catch {
        markSnapshotFailureRef.current(
          event.client_event_id,
          event.occurred_at,
          "upload_failed"
        );
      }
    },
    [assessment_participant_session?.id, assessment_participant_session?.type_test]
  );
  const integrity = useSessionIntegrity({
    product: "psikotest",
    sessionId: assessment_participant_session?.id,
    sessionKind: assessment_participant_session?.type_test,
    running:
      Boolean(assessment_participant_session) &&
      assessment_participant_session?.start_time !== null &&
      timeRemaining !== null &&
      timeRemaining > 0,
    onReturnSync: getSession,
    onStateEvent: captureTabAwaySnapshot,
    onTabAwayEventPersisted: (event) => {
      void uploadTabAwaySnapshot(event);
    },
  });
  markSnapshotFailureRef.current = (sourceClientEventId, occurredAt, reason) =>
    integrity.recordSnapshotFailure(sourceClientEventId, occurredAt, reason);

  // set remaining time remaining
  useEffect(() => {
    if (assessment_participant_session?.remaining_time !== undefined && assessment_participant_session?.remaining_time !== null) {
      setTimeRemaining(assessment_participant_session.remaining_time)
    }
  }, [assessment_participant_session, setTimeRemaining]);


  // to make sure remaining time is correct
  // set remaining time context
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      if (Math.floor(timeRemaining % intervalCheckTimer) === 0) {
        console.log(new Date() + " check timer");
        getSession();
      }
    }
  }, [timeRemaining, getSession]);

  const startSession = async () => {
    try {
      // Start session test
      const psc = new ParticipantSession();
      // make sure not undefined
      if (assessment_participant_session && assessment_participant_session.type_test) {
        await psc.startSession(assessment_participant_session.id, assessment_participant_session.type_test);
      }
      getSession();
    } catch (error) {
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  };

  const closeSession = async () => {
    try {
      const confirm = await Swal.fire({
        title: "Apakah anda yakin?",
        text: "Semua jawaban anda akan dikunci!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, Kunci Jawaban!",
        cancelButtonText: "Batalkan",
      });
      if (confirm.isConfirmed) {
        // Start session test
        const psc = new ParticipantSession();
        // make sure not undefined
        if (assessment_participant_session && assessment_participant_session.type_test) {
          await psc.closeSession(assessment_participant_session.id, assessment_participant_session.type_test);
        }
        getSession();
        // for state close by user
        setCloseByUser(true);
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }

  // counter to capt image webcam
  const getCamera = useCallback(async () => {
    if (timeRemaining !== null && timeRemaining > 0) {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        setCameraAllowed(true);
      } catch (error) {
        setCameraAllowed(false);
      }
      setIsCheckCamera(true);
      // console.log(timeRemaining, intervalGetImage)
      if (Math.floor(timeRemaining % intervalGetImage) === 0) {
        if (webcamRef !== null && webcamRef.current !== null) {
          const image = webcamRef.current.getScreenshot();
          if (image !== null) {
            console.log(new Date(), "capt image");
            setCaptureImage(image);
          }
        }
      }
    } else {
      const stream = webcamRef.current?.stream
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
      }
    }
  }, [timeRemaining, intervalGetImage]);

  // check camera is open when remaining time set
  useEffect(() => {
    getCamera();
  }, [getCamera]);

  // upload capture image
  useEffect(() => {
    const uploadCapturedImage = async () => {
      if (captureImage !== undefined) {
        try {
          // upload webcam
          // make sure not undefined
          if (assessment_participant_session && assessment_participant_session.type_test) {
            const psc = new ParticipantSession();
            await psc.saveWebcam(assessment_participant_session.id, assessment_participant_session.type_test, {
              base64_image: captureImage,
            });
          }
        } catch (error) {
          const errorMessage = errorHandler(error);
          setNotif({ type: "error", message: errorMessage });
        }
      }
    };
    uploadCapturedImage();
  }, [captureImage, setNotif, assessment_participant_session]);

  return (
    <div className="print:hidden w-screen h-screen flex flex-col overflow-hidden">
      <IntegrityWarning message={integrity.warning} />
      <FullscreenReturnBar integrity={integrity} />
      <Header />
      {/* check active session */}
      {isCheckActiveSession === false ? (
        <div className="h-full flex flex-col justify-center items-center">
          <div className="font-light text-lg">Cek Sesi Aktif</div>
        </div>
      ) : (
        <>
          {/* If no session active (open) */}
          {assessment_participant_session === undefined ? (
            <div className="h-full flex flex-col justify-center items-center">
              <div className="font-semibold text-xl">Belum ada sesi dibuka</div>
              <div className="font-light text-lg">Mohon tunggu dan ikuti arahan assessor</div>
            </div>
          ) : (
            <>
              {/* if session active (open) */}
              {/* check if user not start the session (start_time === null)*/}
              {assessment_participant_session.start_time === null ? (
                <div className="h-full flex flex-col justify-center items-center">
                  {questionaryImageLoadFailed !== ""
                    ? <>
                      <div>Loading Questionary Failed</div>
                      <div>{questionaryImageLoadFailed}</div>
                    </>
                    : <>
                      {questionaryWithImageLoad === undefined
                        ? <div>Check Questionary</div>
                        : <>
                          {questionaryWithImageLoad === true
                            ? <>
                              <div>Please Wait</div>
                              <div>Loading Questionary Image</div>
                              <div>{imageQuestionaryTotalLoaded + " / " + imageQuestionaryTotal}</div>
                            </>
                            : <>
                              <ConsentStartPanel
                                integrity={integrity}
                                title="Sudah siap memulai?"
                                onStart={startSession}
                                startLabel="Ya, mulai"
                              />
                            </>
                          }
                        </>
                      }
                    </>
                  }

                </div>
              ) : (
                <ProtectedTestContent
                  integrity={integrity}
                  participantName={assessment_participant.full_name}
                  participantId={assessment_participant.id}
                >
                <div className="h-full bg-white">
                  <Webcam
                    width={200}
                    height={200}
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    className="-z-10 absolute"
                    videoConstraints={{ facingMode: "user" }}
                  />
                  {/* here the user has been start the assessment session */}
                  {/* check if times up */}
                  {timeRemaining !== null && timeRemaining > 0 && (
                    <>
                      {/* loading for checking camera */}
                      {isCheckCamera ? (
                        <>
                          {/* Check if camera on */}
                          {cameraAllowed ? (
                            <div className="h-screen">
                              {assessment_participant_session !== undefined && (
                                <>
                                  {/** Intelligence */}
                                  {assessment_participant_session.type_test === "cfit" &&
                                    <SubtestCfit assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "ist" &&
                                    <SubtestIst assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {/** Personality */}
                                  {assessment_participant_session.type_test === "MBTI" &&
                                    <SubtestMbti assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "PAPI" &&
                                    <SubtestPapi assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "EPPS" &&
                                    <SubtestEpps assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "SRQ-20" &&
                                    <SubtestSrq20 assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "MMPI" &&
                                    <SubtestMmpi assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "BRS" &&
                                    <SubtestBrs assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {/** Behavioural */}
                                  {assessment_participant_session.type_test === "RAC" &&
                                    <SubtestRac assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "DISC" &&
                                    <SubtestDisc assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                  {assessment_participant_session.type_test === "RESILIENCE" &&
                                    <SubtestResilience assessment_participant_session={assessment_participant_session} closeSession={closeSession} />
                                  }
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="h-screen flex flex-col justify-center items-center">
                              <div className="font-semibold text-xl">Akses kamera tidak di izinkan</div>
                              <div className="font-light text-lg text-center">
                                Akses kamera diperlukan untuk melanjutkan proses assessment, mohon berikan izin akses
                                kamera kepada halaman ini.
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="h-screen flex flex-col justify-center items-center">
                          <div className="font-light text-lg text-center">Check Kamera</div>
                        </div>
                      )}
                    </>
                  )}
                  {timeRemaining !== null && timeRemaining <= 0 && (
                    <div className="h-screen flex flex-col justify-center items-center">
                      {close_by_user
                        ? <div className="font-semibold text-xl">Test telah diselesaikan</div>
                        : <div className="font-semibold text-xl">Waktu anda habis</div>
                      }
                      <div className="font-light text-lg">Mohon tunggu dan ikuti arahan assessor</div>
                    </div>
                  )}
                </div>
                </ProtectedTestContent>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Session;
